// ... your same imports
import React, { Component } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { LastLocationProvider } from "react-router-last-location";
import * as workerTimers from "worker-timers";
import Cookies from "js-cookie";
import Modal from "react-modal";
import Amplify, { Auth } from "aws-amplify";
import awsconfig from "./config/aws.exports";
Amplify.configure(awsconfig);

import axios from "./axios/axios";
import AuthenticatedRoutes from "./routes/AuthenticatedRoutes";
import UnauthenticatedRoutes from "./routes/UnauthenticatedRoutes";
import AuthContext from "./contexts/AuthContext";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import "font-awesome/css/font-awesome.min.css";
import "./assets/scss/style.scss";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import "tippy.js/dist/tippy.css";

import KonfhubLoad from "./components/KonfhubLoad";
import { setAccessToken, setJwtToken } from "./revampCRUD/axios";
import { sortEventDetails } from "./Utils/helpers";
import { getOrganiserEventsDetails } from "./revampCRUD/app.crud";
import { errorToaster } from "./reusable/common";

import TagManager from "react-gtm-module";
import ImageUploadContext from "./contexts/ImageUploadContext";
import ImageUploadingModal from "./revampComponents/Atom/Modals/ImageUploadingModal";

const tagManagerArgs = { gtmId: "GTM-KHHN35F" };
TagManager.initialize(tagManagerArgs);
Modal.setAppElement("#root");

const Page404 = React.lazy(() => import("./pages/Page404"));

class App extends Component {
	constructor(props) {
		super(props);
		this.state = {
			user: { attributes: {} },
			signedIn: null,
			organiserDetails: null,
			communityDetails: null,
			eventInfo: {},
			showUploadImageModal: false, //for uploading bulk AI Gallery
			selectedFiles: [], //for uploading bulk AI Gallery
			uploadProgress: {}, //for uploading bulk AI Gallery
			failedFile: [], //for uploading bulk AI Gallery
		};
		this.refreshTimeout = null;
	}

	componentDidMount() {
		this.checkSession();
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
	}

	componentWillUnmount() {
		this.clearTokenRefreshLoop();
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
	}

	componentDidUpdate(prevProps, prevState) {
		if (!prevState.showUploadImageModal && this.state.showUploadImageModal) {
			window.addEventListener("beforeunload", this.handleBeforeUnload);
		} else if (prevState.showUploadImageModal && !this.state.showUploadImageModal) {
			window.removeEventListener("beforeunload", this.handleBeforeUnload);
		}
	}

	handleBeforeUnload = (event) => {
		if (this.state.showUploadImageModal) {
			event.preventDefault();
			event.returnValue = "";
		}
	};

	handleVisibilityChange = async () => {
		if (document.visibilityState === "visible") {
			try {
				const session = await Auth.currentSession();
				setJwtToken(session.getIdToken().getJwtToken());
				setAccessToken(session.getAccessToken().getJwtToken());
			} catch (err) {
				console.error("Session refresh on visibility failed", err);
				window.location.href = "/logout";
			}
		}
	};

	checkSession = async () => {
		let user = { attributes: {} };
		let signedIn = false;
		let organiserDetails = null;

		try {
			user = await Auth.currentAuthenticatedUser({ bypassCache: true });
			const session = await Auth.currentSession();

			Cookies.set("userEmail", user.attributes.email);
			signedIn = true;

			localStorage.setItem("eventId", user.attributes["custom:lastEvent"]);
			setJwtToken(session.getIdToken().getJwtToken());
			setAccessToken(session.getAccessToken().getJwtToken());

			this.startTokenRefreshLoop();
			organiserDetails = await this.getOrganiserDetails();
		} catch (err) {
			console.error("checkSession error", err);
		}

		this.setState({
			user,
			signedIn,
			organiserDetails,
			communityDetails: null,
		});
	};

	startTokenRefreshLoop = async () => {
		this.clearTokenRefreshLoop();
		try {
			const session = await Auth.currentSession();
			const idToken = session.getIdToken();
			const expiryTime = idToken.getExpiration() * 1000;
			const now = Date.now();
			const delay = Math.max(1000, expiryTime - now - 30000); // 30s before expiry

			this.refreshTimeout = workerTimers.setTimeout(async () => {
				try {
					const newSession = await Auth.currentSession(); // triggers refresh
					setJwtToken(newSession.getIdToken().getJwtToken());
					setAccessToken(newSession.getAccessToken().getJwtToken());
					this.startTokenRefreshLoop(); // loop again
				} catch (err) {
					console.error("Token refresh failed", err);
					window.location.href = "/logout";
				}
			}, delay);
		} catch (err) {
			console.error("Error scheduling token refresh", err);
		}
	};

	clearTokenRefreshLoop = () => {
		if (this.refreshTimeout) {
			workerTimers.clearTimeout(this.refreshTimeout);
			this.refreshTimeout = null;
		}
	};

	getOrganiserDetails = async () => {
		try {
			const orgRes = await getOrganiserEventsDetails();
			if (orgRes.status === 200) {
				return sortEventDetails(orgRes.data || []);
			} else if (orgRes.status === 404) {
				errorToaster("There is no user with this Id, please log in again");
				this.setAuth({}, false);
			}
		} catch (err) {
			console.error(err);
			errorToaster("Something went wrong, please try again later");
		}
		return [];
	};

	setAuth = async (user = {}, signedIn = true) => {
		this.clearTokenRefreshLoop();
		let organiserDetails = [];
		if (signedIn) {
			await this.startTokenRefreshLoop();
			organiserDetails = await this.getOrganiserDetails();
		}
		this.setState({ user, signedIn, organiserDetails });
	};

	refreshOrganiserDetails = async () => {
		const organiserDetails = await this.getOrganiserDetails();
		this.setState({ organiserDetails });
	};

	setCommunityDetails = (communityDetails = null) => {
		this.setState({ communityDetails });
	};

	setEventDetails = async (eventInfo) => {
		this.setState({ eventInfo });
	};

	storeGalleryDetails = (selectedFiles) => {
		this.setState({ showUploadImageModal: true, selectedFiles });
	};

	setUploadProgress = (uploadProgress) => {
		this.setState((prevState) => ({
			uploadProgress: { ...prevState.uploadProgress, ...uploadProgress },
		}));
	};

	clearGalleryDetails = () => {
		this.setState({ showUploadImageModal: false, selectedFiles: [], failedFile: [], uploadProgress: {} });
	}

	storeFailedUpload = (fileName) => {
		this.setState((prevState) => ({
			showUploadImageModal: true,
			failedFile: [...(prevState.failedFile || []), fileName]
		}));
	}


	render() {
		const {
			user,
			signedIn,
			organiserDetails,
			communityDetails,
			eventInfo,
			showUploadImageModal,
			selectedFiles,
			uploadProgress,
			manageProfileDetails
		} = this.state;

		return (
			<>
				<div className="content-container h-100 d-flex flex-column">
					<AuthContext.Provider
						value={{
							user,
							signedIn,
							setAuth: this.setAuth,
							eventInfo,
							setEventDetails: this.setEventDetails,
							refreshToken: this.startTokenRefreshLoop,
							organiserDetails,
							communityDetails,
							refreshOrganiserDetails: this.refreshOrganiserDetails,
							setCommunityDetails: this.setCommunityDetails,
						}}
					>
						<ImageUploadContext.Provider
							value={{
								storeGalleryDetails: this.storeGalleryDetails,
								clearGalleryDetails: this.clearGalleryDetails,
								setUploadProgress: this.setUploadProgress,
								storeFailedUpload: this.storeFailedUpload,
							}}
						>
							<ImageUploadingModal
								selectedFiles={this.state.selectedFiles}
								showUploadImageModal={this.state.showUploadImageModal}
								uploadProgress={this.state.uploadProgress}
								failedFile={this.state.failedFile}
								clearGalleryDetails={this.clearGalleryDetails}
							/>

							<BrowserRouter getUserConfirmation={() => { }}>
								<LastLocationProvider>
									<React.Suspense fallback={<KonfhubLoad />}>
										<Switch>
											<Route
												exact
												path="/404"
												name="Page 404"
												render={(props) => <Page404 {...props} />}
											/>
											{signedIn === null ? (
												<KonfhubLoad />
											) : signedIn ? (
												<AuthenticatedRoutes signedIn={signedIn} user={user} />
											) : (
												<UnauthenticatedRoutes signedIn={signedIn} user={user} />
											)}
										</Switch>
									</React.Suspense>
								</LastLocationProvider>
							</BrowserRouter>
						</ImageUploadContext.Provider>
					</AuthContext.Provider>
				</div>
				<style>{`
          .content-container {
            background-color: ${localStorage.getItem("darkMode") ? "#2f2f2f" : "auto"
					};
          }
        `}</style>
			</>
		);
	}
}

export default App;